import { Loader2 } from "lucide-react";
import type { TFunction } from "i18next";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { TaxCategory } from "@fiscalo/shared-types";
import type { TaxOption } from "@/lib/taxOptions";

/** Shared list — see lib/taxOptions. A catalog row stores a bare category, so
 *  it gets the standard set; régimen rates are a per-line choice. */

type ClientSummaryCatalogDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  catalogSku: string;
  setCatalogSku: (v: string) => void;
  catalogDescription: string;
  setCatalogDescription: (v: string) => void;
  catalogUnitPrice: string;
  setCatalogUnitPrice: (v: string) => void;
  /** Régimen-aware option list, resolved by the parent. */
  taxOptions: TaxOption[];
  catalogTaxOptionId: string;
  setCatalogTaxOptionId: (v: string) => void;
  catalogDialogSourceLineCount: number;
  catalogDialogCombinedUyu: number;
  catalogDialogCombinedUsd: number;
  locale: string;
  formatMoney: (currency: string, amount: number, locale: string) => string;
  t: TFunction;
};

export function ClientSummaryCatalogDialog({
  open,
  onOpenChange,
  onSubmit,
  isSubmitting,
  catalogSku,
  setCatalogSku,
  catalogDescription,
  setCatalogDescription,
  catalogUnitPrice,
  setCatalogUnitPrice,
  taxOptions,
  catalogTaxOptionId,
  setCatalogTaxOptionId,
  catalogDialogSourceLineCount,
  catalogDialogCombinedUyu,
  catalogDialogCombinedUsd,
  locale,
  formatMoney,
  t,
}: ClientSummaryCatalogDialogProps): JSX.Element {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground">
            {t("clients.catalog.dialogCreateTitle")}
          </DialogTitle>
          {catalogDialogSourceLineCount > 1 ? (
            <p className="text-sm text-muted-foreground">
              {t("clients.detail.summary.bulkAddDialogHint", {
                count: catalogDialogSourceLineCount,
              })}
            </p>
          ) : null}
          {catalogDialogSourceLineCount > 1 ? (
            <p className="text-sm tabular-nums text-foreground">
              {formatMoney("UYU", catalogDialogCombinedUyu, locale)} ·{" "}
              {formatMoney("USD", catalogDialogCombinedUsd, locale)}
            </p>
          ) : null}
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="summary-catalog-sku">{t("clients.catalog.fields.sku")}</Label>
            <Input
              id="summary-catalog-sku"
              value={catalogSku}
              onChange={(e) => setCatalogSku(e.target.value)}
              placeholder={t("clients.catalog.fields.skuPlaceholder")}
              maxLength={64}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="summary-catalog-desc">{t("clients.catalog.fields.description")}</Label>
            <Input
              id="summary-catalog-desc"
              value={catalogDescription}
              onChange={(e) => setCatalogDescription(e.target.value)}
              placeholder={t("clients.catalog.fields.descriptionPlaceholder")}
              maxLength={500}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="summary-catalog-price">{t("clients.catalog.fields.unitPrice")}</Label>
            <Input
              id="summary-catalog-price"
              type="number"
              min={0}
              step="0.01"
              value={catalogUnitPrice}
              onChange={(e) => setCatalogUnitPrice(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>{t("clients.catalog.fields.defaultTax")}</Label>
            <Select value={catalogTaxOptionId} onValueChange={setCatalogTaxOptionId}>
              <SelectTrigger id="summary-catalog-tax" className="transition-colors duration-150">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {taxOptions.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {t(o.labelKey, o.fallback)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("clients.form.cancel", "Cancel")}
          </Button>
          <Button
            type="button"
            onClick={onSubmit}
            disabled={
              isSubmitting ||
              !catalogDescription.trim() ||
              !Number.isFinite(Number(catalogUnitPrice)) ||
              Number(catalogUnitPrice) < 0
            }
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("clients.catalog.saving")}
              </>
            ) : (
              t("clients.catalog.create")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
